<template>
    <div class="cerfa">

        <!-- ══════════════════════════════════════════════════════════
             SECTION 1 — INSTALLATION & FLUIDE
        ══════════════════════════════════════════════════════════ -->
        <div class="section">
            <div class="section-head">
                <span class="section-bar s-red" />
                <div>
                    <div class="section-title">Installation & Fluide</div>
                    <div class="section-sub">Charge nominale de l'équipement</div>
                </div>
            </div>

            <!-- Charge totale nominale -->
            <div class="field-group">
                <label class="field-label">* Charge totale de l'installation</label>
                <div class="num-wrap" :class="{ 'err': showErrors && !form.fluide_charge_totale_kg }">
                    <input
                        v-model="form.fluide_charge_totale_kg"
                        type="number" inputmode="decimal"
                        class="num-input" placeholder="ex: 2.5"
                        @input="onChargeChange"
                    />
                    <span class="num-unit">kg</span>
                </div>
                <div class="field-hint">Charge nominale inscrite sur la plaque constructeur</div>
            </div>

            <!-- Fluide : sélection via bouteille ou saisie directe -->
            <div class="field-group">
                <label class="field-label">* Fluide frigorigène</label>

                <!-- Si bouteille de charge déjà choisie → fluide verrouillé -->
                <div v-if="form.bouteille_charge_id && selectedFluide" class="fluide-locked">
                    <div class="fluide-locked-info">
                        <div class="fl-code">{{ selectedFluide.code }}</div>
                        <div class="fl-meta">{{ selectedFluide.nom }} · PRG {{ selectedFluide.prg !== null ? selectedFluide.prg : '?' }}</div>
                    </div>
                    <span class="fl-badge">{{ selectedFluide.type }}</span>
                </div>

                <!-- Sinon : recherche libre -->
                <template v-else>
                    <div class="search-wrap" :class="{ 'err': showErrors && !form.fluide_denomination }">
                        <svg class="search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>
                        <input
                            v-model="search"
                            class="search-input"
                            placeholder="Rechercher : R-32, R-410A…"
                            @input="handleSearch"
                            @focus="openDropdown"
                            @blur="onBlur"
                        />
                        <button v-if="selectedFluide" class="clear-btn" @mousedown.prevent="clearFluide">✕</button>
                    </div>

                    <div v-if="showDropdown && filteredFluides.length > 0" class="dropdown">
                        <button
                            v-for="f in filteredFluides"
                            :key="f.code"
                            class="dropdown-item"
                            @mousedown.prevent="selectFluide(f)"
                        >
                            <div class="di-row">
                                <span class="di-code">{{ f.code }}</span>
                                <span class="di-badge" :class="`badge-${f.type.toLowerCase()}`">{{ f.type }}</span>
                            </div>
                            <span class="di-name">{{ f.nom }}</span>
                            <span class="di-prg">PRG {{ f.prg !== null ? f.prg : 'N/A' }}</span>
                        </button>
                    </div>

                    <div v-if="selectedFluide" class="fluide-pill">
                        <div>
                            <div class="fl-code">{{ selectedFluide.code }}</div>
                            <div class="fl-meta">{{ selectedFluide.nom }}</div>
                        </div>
                        <div class="fl-right">
                            <span class="fl-badge">{{ selectedFluide.type }}</span>
                            <span class="fl-prg">PRG {{ selectedFluide.prg !== null ? selectedFluide.prg : '?' }}</span>
                        </div>
                    </div>
                </template>

                <!-- PRG manuel si inconnu -->
                <div v-if="selectedFluide && selectedFluide.prg === null" class="prg-manuel">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#F97316" stroke-width="2" class="warn-ico"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    <div class="prg-content">
                        <div class="prg-label">PRG inconnu — saisir manuellement</div>
                        <div class="num-wrap" style="margin-top:6px">
                            <input v-model="form.fluide_prg_manuel" type="number" inputmode="decimal" class="num-input" placeholder="ex: 2088" @input="computeEqCo2" />
                            <span class="num-unit">GWP</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Récap CO₂ + fréquence (affiché dès que fluide + charge renseignés) -->
            <div v-if="selectedFluide && form.fluide_charge_totale_kg" class="recap-grid">
                <div class="recap-cell">
                    <div class="rc-label">Impact CO₂</div>
                    <div class="rc-value rc-red">{{ form.fluide_tonnage_eq_co2 }} <span class="rc-unit">t éq.</span></div>
                </div>
                <div class="recap-cell">
                    <div class="rc-label">Contrôle obligatoire</div>
                    <div class="rc-value">{{ frequenceControle }}</div>
                </div>
                <div class="recap-cell recap-cell-wide">
                    <div class="rc-label">Catégorie réglementaire</div>
                    <div class="rc-value">{{ categorieReglementaire }}</div>
                </div>
            </div>

            <!-- Système permanent de détection -->
            <button
                class="toggle-card tc-red"
                :class="{ 'tc-on': form.systeme_detection_present }"
                @click="form.systeme_detection_present = !form.systeme_detection_present; computeRegulatoryFields()"
            >
                <div class="tc-body">
                    <div class="tc-title">Système permanent de détection</div>
                    <div class="tc-sub" v-if="selectedFluide && form.fluide_charge_totale_kg">
                        → Fréquence : {{ form.systeme_detection_present ? form.avec_systeme_permanent_detection : form.sans_systeme_permanent_detection }}
                    </div>
                </div>
                <div class="tog-pill" :class="{ 'tog-on-red': form.systeme_detection_present }"><div class="tog-thumb" /></div>
            </button>
        </div>

        <!-- ══════════════════════════════════════════════════════════
             SECTION 2 — NATURE DE L'INTERVENTION
        ══════════════════════════════════════════════════════════ -->
        <div class="section">
            <div class="section-head">
                <span class="section-bar s-orange" />
                <div>
                    <div class="section-title">* Nature de l'intervention</div>
                    <div class="section-sub">Plusieurs choix possibles</div>
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <div
                    v-for="n in natures"
                    :key="n.key"
                    class="flex items-center gap-3 px-4 py-6 rounded-xl border-2 cursor-pointer select-none transition-all duration-150 active:scale-[0.99]"
                    :class="form[n.key]
                        ? 'bg-orange-50 border-orange-300'
                        : 'bg-slate-50 border-slate-200'"
                    @click="toggleNature(n.key)"
                >
                    <div
                        class="w-4 h-4 rounded-full flex-shrink-0 border-[1.5px] transition-all duration-150"
                        :class="form[n.key]
                            ? 'bg-orange-400 border-orange-400'
                            : 'border-slate-400'"
                    />
                    <span
                        class="text-sm transition-colors duration-150"
                        :class="form[n.key]
                            ? 'font-semibold text-orange-700'
                            : 'text-slate-500'"
                    >
                        {{ n.label }}
                    </span>
                </div>
            </div>

            <div v-if="showErrors && !hasNature" class="err-inline">Sélectionnez au moins une nature d'intervention.</div>

            <div v-if="form.intervention_autre" class="field-group" style="margin-top:12px">
                <label class="field-label">Précisez</label>
                <textarea
                    v-model="form.intervention_autre_precisez"
                    class="cerfa-textarea"
                    maxlength="30"
                    rows="2"
                    placeholder="Ex: remplacement vanne…"
                />
                <div class="char-count" :class="{ 'cc-red': form.intervention_autre_precisez.length >= 30 }">
                    {{ form.intervention_autre_precisez.length }}/30
                </div>
            </div>
        </div>

        <!-- ══════════════════════════════════════════════════════════
             SECTION 3 — CONTRÔLE D'ÉTANCHÉITÉ
             Affiché uniquement si contrôle périodique ou non-périodique
        ══════════════════════════════════════════════════════════ -->
        <div v-if="needsControleEtancheite" class="section">
            <div class="section-head">
                <span class="section-bar s-blue" />
                <div>
                    <div class="section-title">Contrôle d'étanchéité</div>
                    <div class="section-sub">Détecteur utilisé pour ce contrôle</div>
                </div>
            </div>

            <!-- Chargement détecteurs -->
            <div v-if="loadingDetecteurs" class="loading-row">
                <span class="spinner" />
                <span>Chargement des détecteurs…</span>
            </div>

            <template v-else>
                <!-- Détecteur actif -->
                <div v-if="selectedDetecteur && !showDetecteurPicker" class="sel-box blue">
                    <div>
                        <div class="sb-label">Détecteur sélectionné</div>
                        <div class="sb-title">{{ selectedDetecteur.marque }} — {{ selectedDetecteur.num_serie }}</div>
                        <div class="sb-sub">Étalonné le {{ formatDate(selectedDetecteur.date_etalonage) }} · Valide jusqu'au {{ formatDate(selectedDetecteur.date_fin_validite) }}</div>
                    </div>
                    <button class="change-btn" @click="showDetecteurPicker = true">Changer</button>
                </div>

                <!-- Liste des détecteurs -->
                <div v-if="!selectedDetecteur || showDetecteurPicker">
                    <div v-if="detecteurs.length > 0" class="sel-list">
                        <button
                            v-for="det in detecteurs"
                            :key="det.id"
                            class="sel-item"
                            :class="{ 'sel-item-on': selectedDetecteur && selectedDetecteur.id === det.id }"
                            @click="applyDetecteur(det); showDetecteurPicker = false"
                        >
                            <div class="si-title">{{ det.marque }} — {{ det.num_serie }}</div>
                            <div class="si-sub">Valide jusqu'au {{ formatDate(det.date_fin_validite) }}</div>
                        </button>
                        <button class="sel-manual" @click="selectedDetecteur = null; showDetecteurPicker = false; form.controle_etancheite_identification = ''; form.controle_etancheite_date = today()">
                            ✏️ &nbsp;Saisie manuelle
                        </button>
                    </div>

                    <!-- Saisie manuelle (aucun détecteur ou choix manuel) -->
                    <div v-if="!selectedDetecteur" class="two-col" style="margin-top:12px">
                        <div class="field-group">
                            <label class="field-label">Date du contrôle</label>
                            <input v-model="form.controle_etancheite_date" type="date" class="cerfa-input" />
                        </div>
                        <div class="field-group">
                            <label class="field-label">ID / N° détecteur</label>
                            <input v-model="form.controle_etancheite_identification" class="cerfa-input" placeholder="Ex: AE-450-Z" />
                        </div>
                    </div>
                </div>
            </template>

            <!-- Fuite -->
            <label class="field-label" style="margin-top:4px">Résultat du contrôle</label>
            <div class="yn-row">
                <button
                    class="yn-btn"
                    :class="form.fuite_oui_non === false ? 'yn-active-green' : 'yn-inactive'"
                    @click="form.fuite_oui_non = false"
                >
                    <span class="yn-icon">✓</span>
                    <span class="yn-text">Aucune fuite</span>
                </button>
                <button
                    class="yn-btn"
                    :class="form.fuite_oui_non === true ? 'yn-active-red' : 'yn-inactive'"
                    @click="form.fuite_oui_non = true"
                >
                    <span class="yn-icon">⚠</span>
                    <span class="yn-text">Fuite détectée</span>
                </button>
            </div>

            <template v-if="form.fuite_oui_non === true">
                <div v-for="n in [1,2,3]" :key="n" class="fuite-card">
                    <div class="fuite-num">Fuite #{{ n }}</div>
                    <input
                        v-model="form[`fuite_${n}_localisation`]"
                        class="cerfa-input"
                        :placeholder="`Localisation (ex: raccord HP, vanne de service…)`"
                        style="margin-bottom:8px"
                    />
                    <div class="repair-row">
                        <button v-for="[val,txt] in repairOpts" :key="val"
                            class="repair-btn"
                            :class="{ 'rb-on': form[`fuite_${n}_reparation`] === val }"
                            @click="form[`fuite_${n}_reparation`] = val"
                        >{{ txt }}</button>
                    </div>
                </div>
                <div v-if="showErrors && !hasFuiteLocalisation" class="err-inline">Indiquez la localisation d'au moins une fuite.</div>
            </template>
        </div>

        <!-- ══════════════════════════════════════════════════════════
             SECTION 4 — CHARGE DE FLUIDE
             Bouteille de charge → fluide déduit automatiquement
        ══════════════════════════════════════════════════════════ -->
        <div class="section">
            <div class="section-head">
                <span class="section-bar s-green" />
                <div>
                    <div class="section-title">Charge de fluide</div>
                </div>
            </div>
            <div class="text-lg mb-3 font-semibold">Du fluide a-t-il été chargé dans l'équipement ?</div>
            <div class="yn-row">
                <button
                    class="yn-btn"
                    :class="hasChargedGas === false ? 'yn-active-slate' : 'yn-inactive'"
                    @click="setHasChargedGas(false)"
                >
                    <span class="yn-text">Non</span>
                </button>
                <button
                    class="yn-btn"
                    :class="hasChargedGas === true ? 'yn-active-green' : 'yn-inactive'"
                    @click="setHasChargedGas(true)"
                >
                    <span class="yn-text">Oui</span>
                </button>
            </div>

            <template v-if="hasChargedGas">

                <!-- Chargement bouteilles -->
                <div v-if="loadingBouteilles" class="loading-row">
                    <span class="spinner" />
                    <span>Chargement des bouteilles…</span>
                </div>

                <template v-else>

                    <!-- Aucune bouteille choisie → afficher liste bouteilles de charge -->
                    <template v-if="!form.bouteille_charge_id">
                        <div class="info-box info-blue" style="margin-bottom:12px">
                            La sélection de la bouteille détermine automatiquement le fluide frigorigène.
                        </div>

                        <div class="field-group">
                            <label class="field-label">Rechercher une bouteille de charge</label>
                            <input v-model="searchBouteilleCharge" class="cerfa-input" placeholder="Numéro de bouteille…" />
                        </div>

                        <div v-if="bouteillesCharge.length === 0" class="info-box">
                            Aucune bouteille de charge disponible{{ selectedFluide ? ' pour ' + selectedFluide.code : '' }}.
                        </div>
                        <div v-else class="sel-list">
                            <button
                                v-for="b in bouteillesCharge"
                                :key="b.id"
                                class="sel-item"
                                @click="selectBouteilleCharge(b)"
                            >
                                <div class="bott-row">
                                    <div>
                                        <div class="si-title">{{ b.numero }}</div>
                                        <div class="si-sub">{{ b.type_gaz }} · {{ b.poids_actuel }} kg disponibles ({{ b.pourcentage_remplissage }}%)</div>
                                    </div>
                                    <div class="bott-badge-wrap">
                                        <span class="bott-badge green">Charge</span>
                                    </div>
                                </div>
                            </button>
                        </div>
                    </template>

                    <!-- Bouteille sélectionnée -->
                    <template v-else>
                        <div class="sel-box green">
                            <div>
                                <div class="sb-label">Bouteille de charge</div>
                                <div class="sb-title">{{ bouteilleCharge.numero }}</div>
                                <div class="sb-sub">{{ bouteilleCharge.type_gaz }} · {{ bouteilleCharge.poids_actuel }} kg</div>
                            </div>
                            <button class="remove-btn" @click="resetBouteilleCharge">✕</button>
                        </div>

                        <!-- Quantités chargées -->
                        <div class="field-label" style="margin-top:4px">Quantités chargées</div>
                        <div class="">
                            <div class="field-group">
                                <label class="field-label-sm">Vierge</label>
                                <div class="num-wrap-sm">
                                    <input v-model="form.qtt_charge_fluide_vierge" type="number" inputmode="decimal" class="num-input" placeholder="0.0" @input="computeChargeTotal" />
                                    <span class="num-unit-sm">kg</span>
                                </div>
                            </div>
                            <div class="field-group">
                                <label class="field-label-sm">Recyclé</label>
                                <div class="num-wrap-sm">
                                    <input v-model="form.qtt_charge_fluide_recycle" type="number" inputmode="decimal" class="num-input" placeholder="0.0" @input="computeChargeTotal" />
                                    <span class="num-unit-sm">kg</span>
                                </div>
                            </div>
                            <div class="field-group">
                                <label class="field-label-sm">Régénéré</label>
                                <div class="num-wrap-sm">
                                    <input v-model="form.qtt_charge_fluide_regenere" type="number" inputmode="decimal" class="num-input" placeholder="0.0" @input="computeChargeTotal" />
                                    <span class="num-unit-sm">kg</span>
                                </div>
                            </div>
                        </div>

                        <div v-if="form.qtt_charge_total > 0" class="total-banner green-banner">
                            <span class="tb-label">Total chargé</span>
                            <span class="tb-value green-val">{{ form.qtt_charge_total }} kg</span>
                        </div>

                        <div v-if="showErrors && form.qtt_charge_total === 0" class="err-inline">
                            Saisissez au moins une quantité chargée.
                        </div>
                    </template>
                </template>
            </template>
        </div>

        <!-- ══════════════════════════════════════════════════════════
             SECTION 5 — RÉCUPÉRATION DE FLUIDE
        ══════════════════════════════════════════════════════════ -->
        <div class="section">
            <div class="section-head">
                <span class="section-bar s-purple" />
                <div>
                    <div class="section-title">Récupération de fluide</div>
                </div>
            </div>

            <div class="text-lg mb-3 font-semibold">Du fluide a-t-il été récupéré ?</div>

            <div class="yn-row">
                <button
                    class="yn-btn"
                    :class="hasRecoveredGas === false ? 'yn-active-slate' : 'yn-inactive'"
                    @click="setHasRecoveredGas(false)"
                >
                    <span class="yn-text">Non</span>
                </button>
                <button
                    class="yn-btn"
                    :class="hasRecoveredGas === true ? 'yn-active-purple' : 'yn-inactive'"
                    @click="setHasRecoveredGas(true)"
                >
                    <span class="yn-text">Oui</span>
                </button>
            </div>

            <template v-if="hasRecoveredGas">

                <div v-if="loadingBouteilles" class="loading-row">
                    <span class="spinner" />
                    <span>Chargement des bouteilles…</span>
                </div>

                <template v-else>
                    <template v-if="!form.bouteille_recuperation_id">
                        <div class="info-box info-purple" style="margin-bottom:12px">
                            Sélectionnez la bouteille de récupération utilisée sur chantier.
                        </div>

                        <div class="field-group">
                            <label class="field-label">Rechercher une bouteille de récupération</label>
                            <input v-model="searchBouteilleRecup" class="cerfa-input" placeholder="Numéro de bouteille…" />
                        </div>

                        <div v-if="bouteillesRecup.length === 0" class="info-box">
                            Aucune bouteille de récupération disponible{{ selectedFluide ? ' pour ' + selectedFluide.code : '' }}.
                        </div>
                        <div v-else class="sel-list">
                            <button
                                v-for="b in bouteillesRecup"
                                :key="b.id"
                                class="sel-item"
                                @click="selectBouteilleRecup(b)"
                            >
                                <div class="bott-row">
                                    <div>
                                        <div class="si-title">{{ b.numero }}</div>
                                        <div class="si-sub">{{ b.type_gaz }} · {{ b.poids_actuel }} kg · capacité {{ b.capacite_kg || '?' }} kg</div>
                                    </div>
                                    <span class="bott-badge purple">Récup.</span>
                                </div>
                            </button>
                        </div>
                    </template>

                    <template v-else>
                        <div class="sel-box purple">
                            <div>
                                <div class="sb-label">Bouteille de récupération</div>
                                <div class="sb-title">{{ bouteilleRecup.numero }}</div>
                                <div class="sb-sub">{{ bouteilleRecup.type_gaz }} · {{ bouteilleRecup.poids_actuel }} kg</div>
                            </div>
                            <button class="remove-btn" @click="resetBouteilleRecuperation">✕</button>
                        </div>

                        <!-- Destination (auto-remplie depuis bouteille) -->
                        <div v-if="form.destination_recuperation_fluide" class="dest-box">
                            <div class="dest-label">Destination de récupération</div>
                            <div class="dest-value">{{ form.destination_recuperation_fluide }}</div>
                        </div>

                        <!-- Quantités récupérées -->
                        <div class="field-label" style="margin-top:4px">Quantités récupérées</div>
                        <div class="">
                            <div class="field-group">
                                <label class="field-label-sm">Traitement</label>
                                <div class="num-wrap-sm">
                                    <input v-model="form.qtt_recuperee_traitement" type="number" inputmode="decimal" class="num-input" placeholder="0.0" @input="computeRecuperationTotal" />
                                    <span class="num-unit-sm">kg</span>
                                </div>
                            </div>
                            <div class="field-group">
                                <label class="field-label-sm">Réutilisation</label>
                                <div class="num-wrap-sm">
                                    <input v-model="form.qtt_recuperee_reutilisation" type="number" inputmode="decimal" class="num-input" placeholder="0.0" @input="computeRecuperationTotal" />
                                    <span class="num-unit-sm">kg</span>
                                </div>
                            </div>
                        </div>

                        <div v-if="form.qtt_recuperee_total > 0" class="total-banner purple-banner">
                            <div>
                                <div class="tb-label">Total récupéré</div>
                                <div class="tb-value purple-val">{{ form.qtt_recuperee_total }} kg</div>
                            </div>
                            <div style="text-align:right">
                                <div class="tb-label">ADR</div>
                                <div class="adr-val">{{ form.fluide_inflammable ? '⚠ Inflammable' : '✓ Non-inflammable' }}</div>
                            </div>
                        </div>

                        <div class="field-group" style="margin-top:12px">
                            <label class="field-label">N° BSFF</label>
                            <input v-model="form.qtt_recuperee_num_bsff" class="cerfa-input" placeholder="Bordereau de suivi des fluides frigorigènes" />
                        </div>
                    </template>
                </template>
            </template>
        </div>

        <!-- ══════════════════════════════════════════════════════════
             ERREURS DE VALIDATION
        ══════════════════════════════════════════════════════════ -->
        <div v-if="showErrors && validation.errors.length > 0" class="err-summary">
            <div class="err-summary-title">⚠ Corrections requises</div>
            <div v-for="(e, i) in validation.errors" :key="i" class="err-row">
                <span class="err-dot" />
                <span class="err-text">{{ e }}</span>
            </div>
        </div>

        <div style="height:32px" />
    </div>
</template>

<script>
// ---------------------------------------------------------------------------
// Fluides — liste de référence (remplacer par import fluidesList si disponible)
// ---------------------------------------------------------------------------
import fluides from '@/config/fluides.js';

const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export default {
    name: 'CerfaForm',
    inject: ['wm'],

    // -------------------------------------------------------------------------
    data() {
        return {
            // Fluide
            search:         '',
            filteredFluides:[],
            showDropdown:   false,
            selectedFluide: null,

            // Détecteurs
            detecteurs:          [],
            selectedDetecteur:   null,
            loadingDetecteurs:   false,
            showDetecteurPicker: false,

            // Bouteilles
            bouteilles:            [],
            loadingBouteilles:     false,
            searchBouteilleCharge: '',
            searchBouteilleRecup:  '',

            // Toggles charge/récup
            hasChargedGas:   null, // null = pas encore répondu
            hasRecoveredGas: null,

            showErrors:  false,
            draftLoaded: false,

            // Formulaire CERFA complet
            form: {
                // Fluide
                fluide_denomination:            '',
                fluide_charge_totale_kg:        '',
                fluide_tonnage_eq_co2:          0,
                fluide_prg_manuel:              '',
                fluide_inflammable:             false,
                fluide_non_inflammable:         false,

                // Natures
                assemblage_equipement:                 false,
                mise_en_service:                       false,
                modification_equipement:               false,
                maintenance_equipement:                false,
                controle_etancheite_periodique:        false,
                controle_etancheite_non_periodique:    false,
                demantelement:                         false,
                intervention_autre:                    false,
                intervention_autre_precisez:           '',

                // Contrôle étanchéité
                controle_etancheite_date:              new Date().toISOString().substr(0, 10),
                controle_etancheite_identification:    '',
                systeme_detection_present:             false,

                // Catégories réglementaires (calculées auto)
                quantite_fluide_hcfc:                  '',
                quantite_fluide_hfc_ppc:               '',
                quantite_fluide_hfo:                   '',
                sans_systeme_permanent_detection:      '',
                avec_systeme_permanent_detection:      '',

                // Fuites
                fuite_oui_non:        null,
                fuite_1_localisation: '', fuite_1_reparation: 0,
                fuite_2_localisation: '', fuite_2_reparation: 0,
                fuite_3_localisation: '', fuite_3_reparation: 0,

                // Charge
                bouteille_charge_id:          null,
                qtt_charge_total:             0,
                qtt_charge_fluide_vierge:     '',
                qtt_charge_fluide_recycle:    '',
                qtt_charge_fluide_regenere:   '',

                // Récupération
                bouteille_recuperation_id:              null,
                qtt_recuperee_total:                    0,
                qtt_recuperee_traitement:               '',
                qtt_recuperee_reutilisation:            '',
                qtt_recuperee_num_bsff:                 '',
                qtt_recuperee_identification_contenant: '',
                destination_recuperation_fluide:        '',
            },

            // Natures de l'intervention
            natures: [
                { key:'assemblage_equipement',              label:'Assemblage initial',        icon:'' },
                { key:'mise_en_service',                    label:'Mise en service',            icon:'' },
                { key:'modification_equipement',            label:'Modification',               icon:'' },
                { key:'maintenance_equipement',             label:'Maintenance',                icon:'' },
                { key:'controle_etancheite_periodique',     label:'Contrôle périodique',        icon:'' },
                { key:'controle_etancheite_non_periodique', label:'Contrôle non-périodique',    icon:'' },
                { key:'demantelement',                      label:'Démantèlement',              icon:'' },
                { key:'intervention_autre',                 label:'Autre',                      icon:''  },
            ],

            repairOpts: [
                [0, 'N/C'],
                [1, 'Réparée'],
                [2, 'À réparer'],
            ],
        };
    },

    // -------------------------------------------------------------------------
    computed: {
        STORAGE_KEY() { return `cerfa_draft_${this.wm.intervention.id}`; },

        // Contrôle d'étanchéité requis seulement pour ces deux natures
        needsControleEtancheite() {
            return this.form.controle_etancheite_periodique || this.form.controle_etancheite_non_periodique;
        },

        hasNature() {
            return this.natures.some(n => this.form[n.key]);
        },

        hasFuiteLocalisation() {
            return this.form.fuite_1_localisation || this.form.fuite_2_localisation || this.form.fuite_3_localisation;
        },

        frequenceControle() {
            const f = this.form.systeme_detection_present
                ? this.form.avec_systeme_permanent_detection
                : this.form.sans_systeme_permanent_detection;
            return f || '—';
        },

        categorieReglementaire() {
            if (!this.selectedFluide) return '—';
            const type  = this.selectedFluide.type;
            const tCo2  = this.form.fluide_tonnage_eq_co2;
            const kg    = parseFloat(this.form.fluide_charge_totale_kg) || 0;

            if (type === 'HCFC') {
                if (kg < 2)   return 'Cat. 0 — < 2 kg HCFC (non soumis)';
                if (kg < 30)  return 'Cat. 1 — 2 à 30 kg HCFC';
                if (kg < 300) return 'Cat. 2 — 30 à 300 kg HCFC';
                return          'Cat. 3 — > 300 kg HCFC';
            }
            if (['HFC','HFO','PFC','Mélange'].includes(type)) {
                if (tCo2 < 5)   return 'Cat. 0 — < 5 t éq. CO₂ (non soumis)';
                if (tCo2 < 50)  return 'Cat. 1 — 5 à 50 t éq. CO₂';
                if (tCo2 < 500) return 'Cat. 2 — 50 à 500 t éq. CO₂';
                return            'Cat. 3 — > 500 t éq. CO₂';
            }
            return '—';
        },

        validation() {
            const errors = [];
            const f = this.form;

            if (!f.fluide_denomination || !this.selectedFluide)
                errors.push('Le fluide frigorigène est obligatoire.');
            if (!f.fluide_charge_totale_kg || parseFloat(f.fluide_charge_totale_kg) <= 0)
                errors.push('La charge totale de l\'installation est obligatoire.');
            if (this.selectedFluide?.prg === null && !f.fluide_prg_manuel)
                errors.push('Le PRG du fluide est inconnu, saisissez-le manuellement.');
            if (!this.hasNature)
                errors.push('Au moins une nature d\'intervention est requise.');
            if (f.intervention_autre && !f.intervention_autre_precisez)
                errors.push('Précisez la nature "Autre" de l\'intervention.');

            if (this.needsControleEtancheite) {
                if (!f.controle_etancheite_date)
                    errors.push('La date du contrôle d\'étanchéité est obligatoire.');
                if (!f.controle_etancheite_identification)
                    errors.push('L\'identification du détecteur est obligatoire.');
                if (f.fuite_oui_non === null)
                    errors.push('Indiquez si une fuite a été détectée.');
                if (f.fuite_oui_non === true && !this.hasFuiteLocalisation)
                    errors.push('Indiquez la localisation d\'au moins une fuite détectée.');
            }

            // Charge — réponse obligatoire (null = pas encore répondu)
            if (this.hasChargedGas === null)
                errors.push('Indiquez si du fluide a été chargé.');
            if (this.hasChargedGas === true) {
                if (!f.bouteille_charge_id)
                    errors.push('Sélectionnez la bouteille de charge utilisée.');
                if (f.bouteille_charge_id && f.qtt_charge_total <= 0)
                    errors.push('Saisissez la quantité de fluide chargée.');
            }

            // Récupération — réponse obligatoire (null = pas encore répondu)
            if (this.hasRecoveredGas === null)
                errors.push('Indiquez si du fluide a été récupéré.');
            if (this.hasRecoveredGas === true) {
                if (!f.bouteille_recuperation_id)
                    errors.push('Sélectionnez la bouteille de récupération utilisée.');
                if (f.bouteille_recuperation_id && f.qtt_recuperee_total <= 0)
                    errors.push('Saisissez la quantité de fluide récupérée.');
            }

            return { errors };
        },

        isCerfaValid() {
            return this.validation.errors.length === 0;
        },

        // Bouteilles filtrées par fluide sélectionné
        bouteillesByFluide() {
            if (!this.selectedFluide) return this.bouteilles; // toutes si pas de fluide choisi
            const code = normalize(this.selectedFluide.code);
            return this.bouteilles.filter(b => normalize(b.type_gaz) === code);
        },

        bouteillesCharge() {
            const list = this.bouteillesByFluide.filter(b => b.etat === 'charge');
            const q = this.searchBouteilleCharge.trim().toLowerCase();
            return q ? list.filter(b => (b.numero || '').toLowerCase().includes(q)) : list;
        },

        bouteillesRecup() {
            const list = this.bouteillesByFluide.filter(b =>
                ['recuperation', 'récupération'].includes((b.etat || '').toLowerCase())
            );
            const q = this.searchBouteilleRecup.trim().toLowerCase();
            return q ? list.filter(b => (b.numero || '').toLowerCase().includes(q)) : list;
        },

        bouteilleCharge() {
            return this.bouteilles.find(b => b.id === this.form.bouteille_charge_id) || {};
        },

        bouteilleRecup() {
            return this.bouteilles.find(b => b.id === this.form.bouteille_recuperation_id) || {};
        },
    },

    // -------------------------------------------------------------------------
    watch: {
        form: {
            deep: true,
            handler(val) {
                if (!this.draftLoaded) return;
                try {
                    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
                        form:           val,
                        selectedFluide: this.selectedFluide,
                        hasChargedGas:  this.hasChargedGas,
                        hasRecoveredGas:this.hasRecoveredGas,
                    }));
                } catch {}
            },
        },
    },

    // -------------------------------------------------------------------------
    created() {
        this.loadDraft();
        this.fetchDetecteurs();
        this.fetchBouteilles();
    },

    mounted() {
        // Expose la validité du CERFA à WorkMode via wm
        // WorkMode peut alors bloquer le bouton "Continuer" tant que le CERFA est incomplet
        this.wm._cerfaFormInstance = this;
    },

    beforeDestroy() {
        if (this.wm._cerfaFormInstance === this) {
            this.wm._cerfaFormInstance = null;
        }
    },

    // -------------------------------------------------------------------------
    methods: {
        today() { return new Date().toISOString().substr(0, 10); },

        formatDate(d) {
            if (!d) return '—';
            try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return d; }
        },

        // ── Draft ─────────────────────────────────────────────────────────────
        loadDraft() {
            try {
                const raw = localStorage.getItem(this.STORAGE_KEY);
                if (raw) {
                    const p = JSON.parse(raw);
                    if (p.form)            this.form            = { ...this.form, ...p.form };
                    if (p.selectedFluide)  { this.selectedFluide = p.selectedFluide; this.search = p.selectedFluide.code || ''; }
                    if (p.hasChargedGas  !== undefined) this.hasChargedGas   = p.hasChargedGas;
                    if (p.hasRecoveredGas !== undefined) this.hasRecoveredGas = p.hasRecoveredGas;
                }
            } catch {}
            this.draftLoaded = true;
        },

        // ── Fluide ────────────────────────────────────────────────────────────
        handleSearch() {
            const q = normalize(this.search);
            if (!q) { this.filteredFluides = []; this.showDropdown = false; return; }
            this.filteredFluides = fluides.filter(f =>
                normalize(f.code).includes(q) || normalize(f.nom).includes(q)
            ).slice(0, 12);
            this.showDropdown = this.filteredFluides.length > 0;
        },

        openDropdown() {
            if (this.filteredFluides.length > 0) this.showDropdown = true;
        },

        selectFluide(f) {
            this.selectedFluide              = f;
            this.search                      = f.code;
            this.showDropdown                = false;
            this.form.fluide_denomination    = f.code;
            this.form.fluide_inflammable     = !!f.inflammable;
            this.form.fluide_non_inflammable = !f.inflammable;
            this.computeEqCo2();
        },

        clearFluide() {
            // Ne pas effacer si le fluide est verrouillé par la bouteille
            if (this.form.bouteille_charge_id) return;
            this.selectedFluide           = null;
            this.search                   = '';
            this.form.fluide_denomination = '';
            this.filteredFluides          = [];
        },

        onBlur() { setTimeout(() => { this.showDropdown = false; }, 150); },

        // ── Calculs réglementaires ────────────────────────────────────────────
        onChargeChange() {
            this.computeEqCo2();
        },

        computeEqCo2() {
            const charge = parseFloat(this.form.fluide_charge_totale_kg);
            if (!this.selectedFluide || !charge) { this.form.fluide_tonnage_eq_co2 = 0; this.computeRegulatoryFields(); return; }
            const prg = this.selectedFluide.prg !== null
                ? this.selectedFluide.prg
                : (parseFloat(this.form.fluide_prg_manuel) || 0);
            this.form.fluide_tonnage_eq_co2 = Number(((charge * prg) / 1000).toFixed(3));
            this.computeRegulatoryFields();
        },

        computeRegulatoryFields() {
            if (!this.selectedFluide) return;
            const type  = this.selectedFluide.type;
            const kg    = parseFloat(this.form.fluide_charge_totale_kg) || 0;
            const tCo2  = this.form.fluide_tonnage_eq_co2 || 0;

            let q_hcfc = '', q_hfc = '', q_hfo = '';

            if (type === 'HCFC') {
                q_hcfc = kg < 2 ? '0' : kg < 30 ? '1' : kg < 300 ? '2' : '3';
            } else if (['HFC','PFC','Mélange'].includes(type)) {
                q_hfc = tCo2 < 5 ? '0' : tCo2 < 50 ? '1' : tCo2 < 500 ? '2' : '3';
            } else if (type === 'HFO') {
                q_hfo = tCo2 < 5 ? '0' : tCo2 < 50 ? '1' : tCo2 < 500 ? '2' : '3';
            }

            this.form.quantite_fluide_hcfc    = q_hcfc;
            this.form.quantite_fluide_hfc_ppc = q_hfc;
            this.form.quantite_fluide_hfo     = q_hfo;
            this.form.sans_systeme_permanent_detection = this.calcFrequency(false);
            this.form.avec_systeme_permanent_detection = this.calcFrequency(true);
        },

        // Règlement EU 517/2014 (HFC/HFO) & EU 1005/2009 (HCFC)
        calcFrequency(avecDetecteur) {
            const kg   = parseFloat(this.form.fluide_charge_totale_kg) || 0;
            const tCo2 = this.form.fluide_tonnage_eq_co2 || 0;
            if (!this.selectedFluide || !kg) return '';
            const type = this.selectedFluide.type;

            // HFC / HFO / PFC / Mélanges → seuils en tonnes CO₂ éq.
            if (['HFC','HFO','PFC','Mélange'].includes(type)) {
                if (tCo2 < 5)   return 'Non obligatoire (< 5 t éq. CO₂)';
                if (tCo2 < 50)  return avecDetecteur ? 'Tous les 24 mois' : 'Tous les 12 mois';
                if (tCo2 < 500) return avecDetecteur ? 'Tous les 12 mois' : 'Tous les 6 mois';
                return            avecDetecteur ? 'Tous les 6 mois'  : 'Tous les 3 mois';
            }
            // HCFC → seuils en kg
            if (type === 'HCFC') {
                if (kg < 2)   return 'Non obligatoire (< 2 kg)';
                if (kg < 30)  return 'Tous les 12 mois';
                if (kg < 300) return 'Tous les 6 mois';
                return          'Tous les 3 mois';
            }
            // HC, CO2, NH3 → pas de contrôle réglementaire obligatoire (règl. F-gaz ne s'applique pas)
            if (['HC','CO2','NH3'].includes(type)) return 'Non soumis (fluide naturel)';
            return 'Non déterminé';
        },

        // ── Nature ────────────────────────────────────────────────────────────
        toggleNature(key) {
            this.$set(this.form, key, !this.form[key]);
            // Si on décoche contrôle étanchéité, réinitialiser détecteur
            if ((key === 'controle_etancheite_periodique' || key === 'controle_etancheite_non_periodique')
                && !this.needsControleEtancheite) {
                this.selectedDetecteur = null;
            }
        },

        // ── Détecteurs ────────────────────────────────────────────────────────
        async fetchDetecteurs() {
            this.loadingDetecteurs = true;
            try {
                const res = await this.$request.post('/cerfa/get-detecteurs', {});
                this.detecteurs = res?.detecteurs || [];
                const auto = res?.recommanded || (this.detecteurs.length === 1 ? this.detecteurs[0] : null);
                if (auto && !this.selectedDetecteur) this.applyDetecteur(auto);
            } catch (e) {
                console.warn('[CERFA] Erreur chargement détecteurs', e);
            } finally {
                this.loadingDetecteurs = false;
            }
        },

        applyDetecteur(det) {
            this.selectedDetecteur = det;
            this.form.controle_etancheite_identification = `${det.marque} - ${det.num_serie}`;
            this.form.controle_etancheite_date = det.date_etalonage
                ? det.date_etalonage.substr(0, 10) : this.today();
        },

        // ── Bouteilles ────────────────────────────────────────────────────────
        async fetchBouteilles() {
            this.loadingBouteilles = true;
            try {
                const res = await this.$request.get('/bouteilles/mobile');
                this.bouteilles = res || [];
            } catch (e) {
                console.warn('[CERFA] Erreur chargement bouteilles', e);
            } finally {
                this.loadingBouteilles = false;
            }
        },

        setHasChargedGas(val) {
            this.hasChargedGas = val;
            if (!val) this.resetBouteilleCharge();
        },

        setHasRecoveredGas(val) {
            this.hasRecoveredGas = val;
            if (!val) this.resetBouteilleRecuperation();
        },

        // Sélection bouteille de charge → fluide déduit automatiquement
        selectBouteilleCharge(b) {
            this.form.bouteille_charge_id = b.id;
            // Déduire le fluide depuis la bouteille
            const fluide = fluides.find(f => normalize(f.code) === normalize(b.type_gaz));
            if (fluide) this.selectFluide(fluide);
            // Si fluide non trouvé dans la liste locale → on force le code
            else {
                this.form.fluide_denomination    = b.type_gaz || '';
                this.form.fluide_inflammable     = false;
                this.form.fluide_non_inflammable = true;
            }
        },

        resetBouteilleCharge() {
            this.form.bouteille_charge_id        = null;
            this.form.qtt_charge_fluide_vierge   = '';
            this.form.qtt_charge_fluide_recycle  = '';
            this.form.qtt_charge_fluide_regenere = '';
            this.form.qtt_charge_total           = 0;
            this.searchBouteilleCharge           = '';
            // Ne pas effacer le fluide : il peut avoir été saisi manuellement
        },

        selectBouteilleRecup(b) {
            this.form.bouteille_recuperation_id              = b.id;
            this.form.qtt_recuperee_identification_contenant = b.numero || '';
            this.form.destination_recuperation_fluide        = b.adresse_recuperation
                ? `${b.adresse_recuperation.adresse1}, ${b.adresse_recuperation.code_postal} ${b.adresse_recuperation.ville}`
                : (b.site_recuperation || '');
        },

        resetBouteilleRecuperation() {
            this.form.bouteille_recuperation_id              = null;
            this.form.qtt_recuperee_traitement               = '';
            this.form.qtt_recuperee_reutilisation            = '';
            this.form.qtt_recuperee_total                    = 0;
            this.form.qtt_recuperee_num_bsff                 = '';
            this.form.qtt_recuperee_identification_contenant = '';
            this.form.destination_recuperation_fluide        = '';
            this.searchBouteilleRecup                        = '';
        },

        computeChargeTotal() {
            this.form.qtt_charge_total = Number((
                (parseFloat(this.form.qtt_charge_fluide_vierge)   || 0) +
                (parseFloat(this.form.qtt_charge_fluide_recycle)  || 0) +
                (parseFloat(this.form.qtt_charge_fluide_regenere) || 0)
            ).toFixed(3));
        },

        computeRecuperationTotal() {
            this.form.qtt_recuperee_total = Number((
                (parseFloat(this.form.qtt_recuperee_traitement)    || 0) +
                (parseFloat(this.form.qtt_recuperee_reutilisation) || 0)
            ).toFixed(3));
        },
    },
};
</script>

<style scoped>
/* ── Base ───────────────────────────────────────────────────── */
.cerfa { display:flex; flex-direction:column; }

/* ── Section ────────────────────────────────────────────────── */
.section {
    background:#FFF;
    border-radius:20px;
    padding:18px 16px 20px;
    margin-bottom:14px;
    border:1px solid #F1F5F9;
    box-shadow:0 1px 6px rgba(0,0,0,0.05);
}
.section-head  { display:flex; align-items:flex-start; gap:12px; margin-bottom:20px; }
.section-bar   { width:5px; border-radius:4px; flex-shrink:0; align-self:stretch; }
.s-red    { background:#EF4444; }
.s-orange { background:#F97316; }
.s-blue   { background:#3B82F6; }
.s-green  { background:#10B981; }
.s-purple { background:#8B5CF6; }
.section-title { font-size:16px; font-weight:800; color:#1E293B; line-height:1.2; }
.section-sub   { font-size:12px; font-weight:500; color:#94A3B8; margin-top:2px; }

/* ── Labels ─────────────────────────────────────────────────── */
.field-label {
    display:block;
    font-size:10px; font-weight:900;
    color:#64748B; text-transform:uppercase; letter-spacing:0.08em;
    margin-bottom:8px;
}
.field-label-sm {
    display:block;
    font-size:9px; font-weight:900;
    color:#94A3B8; text-transform:uppercase; letter-spacing:0.06em;
    margin-bottom:4px;
}
.field-hint { font-size:11px; color:#94A3B8; margin-top:5px; font-style:italic; }
.field-group { margin-bottom:16px; }

/* ── Input de base ──────────────────────────────────────────── */
.cerfa-input {
    width:100%; box-sizing:border-box;
    height:52px;
    border:2px solid #E2E8F0; border-radius:14px;
    padding:0 16px;
    font-size:16px; font-weight:700; color:#1E293B;
    background:#F8FAFC; outline:none;
    font-family:inherit; -webkit-appearance:none;
    transition:border-color 0.15s, background 0.15s;
    display:block;
}
.cerfa-input:focus { border-color:#2563EB; background:#FFF; }
.cerfa-textarea {
    width:100%; box-sizing:border-box;
    border:2px solid #E2E8F0; border-radius:14px;
    padding:14px 16px;
    font-size:15px; font-weight:500; color:#1E293B;
    background:#F8FAFC; outline:none; font-family:inherit;
    resize:vertical; min-height:72px;
    transition:border-color 0.15s;
}
.cerfa-textarea:focus { border-color:#2563EB; background:#FFF; }
.err { border-color:#EF4444 !important; background:#FEF2F2 !important; }

/* ── Num input ──────────────────────────────────────────────── */
.num-wrap {
    display:flex; align-items:center;
    height:52px; border:2px solid #E2E8F0; border-radius:14px;
    background:#F8FAFC; overflow:hidden; transition:border-color 0.15s;
}
.num-wrap:focus-within { border-color:#2563EB; background:#FFF; }
.num-wrap-sm {
    display:flex; align-items:center;
    height:48px; border:2px solid #E2E8F0; border-radius:12px;
    background:#F8FAFC; overflow:hidden;
}
.num-wrap-sm:focus-within { border-color:#2563EB; background:#FFF; }
.num-input {
    flex:1; border:none; background:transparent;
    font-size:18px; font-weight:800; color:#1E293B;
    outline:none; padding:0 14px; font-family:inherit;
    -webkit-appearance:none; min-width:0;
}
.num-unit {
    padding:0 14px; font-size:13px; font-weight:700; color:#94A3B8;
    background:#F1F5F9; height:100%; display:flex; align-items:center;
    flex-shrink:0; border-left:2px solid #E2E8F0;
}
.num-unit-sm {
    padding:0 10px; font-size:12px; font-weight:700; color:#94A3B8;
    background:#F1F5F9; height:100%; display:flex; align-items:center;
    flex-shrink:0; border-left:1px solid #E2E8F0;
}

/* ── Search ─────────────────────────────────────────────────── */
.search-wrap {
    display:flex; align-items:center; gap:10px;
    height:52px; border:2px solid #E2E8F0; border-radius:14px;
    padding:0 14px; background:#F8FAFC; transition:border-color 0.15s;
}
.search-wrap:focus-within { border-color:#2563EB; background:#FFF; }
.search-ico   { width:18px; height:18px; color:#94A3B8; flex-shrink:0; }
.search-input { flex:1; border:none; background:transparent; font-size:16px; font-weight:700; color:#1E293B; outline:none; font-family:inherit; }
.clear-btn    { width:30px; height:30px; border-radius:50%; border:none; background:#E2E8F0; color:#64748B; font-size:14px; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

/* ── Dropdown ───────────────────────────────────────────────── */
.dropdown {
    margin-top:6px; background:#FFF;
    border:2px solid #E2E8F0; border-radius:16px;
    box-shadow:0 8px 28px rgba(0,0,0,0.12);
    overflow:hidden; max-height:300px; overflow-y:auto; z-index:10; position:relative;
}
.dropdown-item {
    width:100%; display:block; padding:14px 16px;
    border:none; background:transparent; cursor:pointer; text-align:left;
    border-bottom:1px solid #F1F5F9; font-family:inherit;
}
.dropdown-item:last-child { border-bottom:none; }
.dropdown-item:active { background:#EFF6FF; }
.di-row  { display:flex; align-items:center; justify-content:space-between; margin-bottom:2px; }
.di-code { font-size:17px; font-weight:900; color:#111827; }
.di-badge{ font-size:10px; font-weight:700; padding:2px 8px; border-radius:8px; }
.badge-hfc  { background:#DBEAFE; color:#1D4ED8; }
.badge-hcfc { background:#FEF3C7; color:#92400E; }
.badge-hfo  { background:#D1FAE5; color:#065F46; }
.badge-hc   { background:#FEE2E2; color:#991B1B; }
.badge-co2  { background:#E0E7FF; color:#3730A3; }
.badge-nh3  { background:#FCE7F3; color:#9D174D; }
.badge-mélange { background:#F3F4F6; color:#374151; }
.di-name { font-size:12px; color:#64748B; display:block; }
.di-prg  { font-size:11px; color:#94A3B8; display:block; margin-top:1px; }

/* ── Fluide locked / pill ───────────────────────────────────── */
.fluide-locked, .fluide-pill {
    display:flex; align-items:center; justify-content:space-between;
    background:#EFF6FF; border:2px solid #BFDBFE; border-radius:14px;
    padding:13px 16px; margin-top:8px;
}
.fl-code  { font-size:18px; font-weight:900; color:#1D4ED8; }
.fl-meta  { font-size:12px; color:#60A5FA; margin-top:2px; }
.fl-right { display:flex; flex-direction:column; align-items:flex-end; gap:4px; }
.fl-badge { font-size:11px; font-weight:700; background:#DBEAFE; color:#1D4ED8; padding:4px 10px; border-radius:8px; white-space:nowrap; }
.fl-prg   { font-size:11px; color:#93C5FD; }

/* ── PRG manuel ─────────────────────────────────────────────── */
.prg-manuel {
    display:flex; align-items:flex-start; gap:12px;
    background:#FFF7ED; border:2px solid #FED7AA; border-radius:14px;
    padding:14px; margin-top:10px;
}
.warn-ico   { width:20px; height:20px; flex-shrink:0; margin-top:2px; }
.prg-content{ flex:1; }
.prg-label  { font-size:12px; font-weight:700; color:#C2410C; }

/* ── Recap grid ─────────────────────────────────────────────── */
.recap-grid {
    display:grid; grid-template-columns:1fr 1fr;
    gap:8px; margin:14px 0;
}
.recap-cell { background:#F8FAFC; border:1px solid #E2E8F0; border-radius:12px; padding:12px; }
.recap-cell-wide { grid-column:1/-1; }
.rc-label { font-size:9px; font-weight:900; color:#94A3B8; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:4px; }
.rc-value { font-size:14px; font-weight:800; color:#1E293B; line-height:1.3; }
.rc-red   { color:#DC2626; }
.rc-unit  { font-size:11px; font-weight:600; color:#94A3B8; }

/* ── Toggle card ────────────────────────────────────────────── */
.toggle-card {
    display:flex; align-items:center; justify-content:space-between;
    width:100%; padding:14px 16px;
    border:2px solid; border-radius:16px;
    cursor:pointer; text-align:left; gap:12px; min-height:60px;
    transition:all 0.15s; font-family:inherit;
}
.tc-red            { background:#FEF2F2; border-color:#FEE2E2; }
.tc-on.tc-red      { background:#FEF2F2; border-color:#FCA5A5; }
.tc-body { flex:1; }
.tc-title{ font-size:15px; font-weight:700; color:#1E293B; }
.tc-sub  { font-size:11px; color:#6B7280; margin-top:3px; }

/* ── Toggle pill ────────────────────────────────────────────── */
.tog-pill {
    width:54px; height:30px; border-radius:15px; background:#CBD5E1;
    position:relative; flex-shrink:0; transition:background 0.2s;
}
.tog-on-red    { background:#EF4444; }
.tog-on-green  { background:#10B981; }
.tog-on-purple { background:#8B5CF6; }
.tog-thumb {
    position:absolute; top:3px; left:3px;
    width:24px; height:24px; border-radius:50%;
    background:#FFF; box-shadow:0 1px 4px rgba(0,0,0,0.25);
    transition:left 0.2s;
}
.tog-on-red .tog-thumb,
.tog-on-green .tog-thumb,
.tog-on-purple .tog-thumb { left:27px; }

/* ── Nature grid ────────────────────────────────────────────── */
.nature-grid { display:flex; flex-wrap:wrap; gap:8px; }
.nature-btn {
    width:calc(33.333% - 6px); min-height:80px;
    padding:14px 8px 12px;
    background:#F8FAFC; border:2px solid #E2E8F0; border-radius:16px;
    display:flex; flex-direction:column; align-items:flex-start; gap:8px;
    cursor:pointer; transition:all 0.15s; font-family:inherit;
}
.nature-btn:active { transform:scale(0.96); }
.nb-on   { background:#F97316; border-color:#F97316; }
.nb-ico  { font-size:20px; }
.nb-label{ font-size:10px; font-weight:800; color:#64748B; text-transform:uppercase; line-height:1.2; text-align:left; }
.nb-on .nb-label { color:#FFF; }

/* ── OUI/NON ────────────────────────────────────────────────── */
.yn-row { display:flex; gap:10px; margin:8px 0 14px; }
.yn-btn {
    flex:1; min-height:64px;
    border-radius:14px; border:2px solid;
    display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px;
    cursor:pointer; font-family:inherit; transition:all 0.15s;
    padding:10px 8px;
}
.yn-btn:active { transform:scale(0.96); }

/* État neutre — rien sélectionné */
.yn-inactive {
    background:#F8FAFC;
    border-color:#E2E8F0;
}
.yn-inactive .yn-icon { font-size:20px; color:#CBD5E1; }
.yn-inactive .yn-text { font-size:13px; font-weight:700; color:#94A3B8; }

/* Sélectionné — vert */
.yn-active-green {
    background:#059669;
    border-color:#059669;
    box-shadow:0 4px 12px rgba(5,150,105,0.35);
}
.yn-active-green .yn-icon { font-size:22px; color:#FFF; }
.yn-active-green .yn-text { font-size:13px; font-weight:800; color:#FFF; }

/* Sélectionné — rouge */
.yn-active-red {
    background:#DC2626;
    border-color:#DC2626;
    box-shadow:0 4px 12px rgba(220,38,38,0.35);
}
.yn-active-red .yn-icon { font-size:22px; color:#FFF; }
.yn-active-red .yn-text { font-size:13px; font-weight:800; color:#FFF; }

/* Sélectionné — slate (Non) */
.yn-active-slate {
    background:#334155;
    border-color:#334155;
    box-shadow:0 4px 12px rgba(51,65,85,0.3);
}
.yn-active-slate .yn-icon { font-size:20px; color:#FFF; }
.yn-active-slate .yn-text { font-size:13px; font-weight:800; color:#FFF; }

/* Sélectionné — violet */
.yn-active-purple {
    background:#7C3AED;
    border-color:#7C3AED;
    box-shadow:0 4px 12px rgba(124,58,237,0.35);
}
.yn-active-purple .yn-icon { font-size:22px; color:#FFF; }
.yn-active-purple .yn-text { font-size:13px; font-weight:800; color:#FFF; }

.yn-icon { font-size:20px; line-height:1; }
.yn-text  { font-size:13px; font-weight:700; text-align:center; line-height:1.2; }

/* ── Fuite card ─────────────────────────────────────────────── */
.fuite-card {
    background:#EFF6FF; border:2px solid #DBEAFE; border-radius:16px;
    padding:14px; margin-bottom:10px;
}
.fuite-num { font-size:11px; font-weight:900; color:#3B82F6; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:10px; }
.repair-row { display:flex; gap:8px; }
.repair-btn {
    flex:1; min-height:48px; border-radius:12px; border:2px solid #DBEAFE;
    background:#FFF; font-size:13px; font-weight:700; color:#60A5FA;
    cursor:pointer; font-family:inherit; transition:all 0.15s;
}
.rb-on { background:#3B82F6; border-color:#3B82F6; color:#FFF; }

/* ── Select list ────────────────────────────────────────────── */
.sel-list {
    background:#F8FAFC; border:2px solid #E2E8F0; border-radius:16px;
    overflow:hidden; margin-bottom:12px;
}
.sel-item {
    width:100%; display:block; padding:16px; border:none; background:transparent;
    border-bottom:1px solid #E5E7EB; cursor:pointer; text-align:left;
    min-height:64px; font-family:inherit; transition:background 0.1s;
}
.sel-item:last-child { border-bottom:none; }
.sel-item:active, .sel-item-on { background:#EFF6FF; }
.sel-manual {
    width:100%; padding:14px 16px; background:#F8FAFC;
    border:none; border-top:2px solid #E5E7EB;
    cursor:pointer; font-family:inherit; text-align:left;
    font-size:13px; font-weight:700; color:#475569; display:block;
}
.si-title { font-size:15px; font-weight:700; color:#1E293B; }
.si-sub   { font-size:12px; font-weight:600; color:#64748B; margin-top:2px; }

/* ── Selected box ───────────────────────────────────────────── */
.sel-box {
    display:flex; align-items:center; justify-content:space-between;
    border-radius:16px; padding:14px 16px; margin-bottom:14px; border:2px solid;
}
.sel-box.blue   { background:#EFF6FF; border-color:#BFDBFE; }
.sel-box.green  { background:#ECFDF5; border-color:#A7F3D0; }
.sel-box.purple { background:#F5F3FF; border-color:#C4B5FD; }
.sb-label { font-size:9px; font-weight:900; color:#64748B; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:3px; }
.sb-title { font-size:15px; font-weight:800; color:#1E293B; }
.sb-sub   { font-size:12px; font-weight:600; color:#64748B; margin-top:2px; }
.change-btn { padding:10px 16px; background:#2563EB; color:#FFF; border:none; border-radius:12px; font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; flex-shrink:0; }
.remove-btn { width:40px; height:40px; border-radius:50%; border:none; background:#EF4444; color:#FFF; font-size:18px; font-weight:900; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

/* ── Bouteille row ──────────────────────────────────────────── */
.bott-row { display:flex; align-items:center; justify-content:space-between; gap:8px; }
.bott-badge-wrap { flex-shrink:0; }
.bott-badge {
    font-size:10px; font-weight:800; padding:4px 10px; border-radius:8px; white-space:nowrap;
}
.bott-badge.green  { background:#D1FAE5; color:#065F46; }
.bott-badge.purple { background:#EDE9FE; color:#5B21B6; }

/* ── Destination box ────────────────────────────────────────── */
.dest-box {
    background:#F0FDF4; border:2px solid #A7F3D0; border-radius:12px;
    padding:12px 16px; margin-bottom:14px;
}
.dest-label { font-size:10px; font-weight:900; color:#065F46; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:4px; }
.dest-value { font-size:13px; font-weight:600; color:#1E293B; }

/* ── Total banner ───────────────────────────────────────────── */
.total-banner {
    display:flex; align-items:center; justify-content:space-between;
    border-radius:16px; padding:16px; margin-top:8px; border:2px solid;
}
.green-banner  { background:#ECFDF5; border-color:#A7F3D0; }
.purple-banner { background:#F5F3FF; border-color:#C4B5FD; }
.tb-label  { font-size:10px; font-weight:900; color:#6B7280; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:3px; }
.tb-value  { font-size:24px; font-weight:900; }
.green-val  { color:#059669; }
.purple-val { color:#7C3AED; }
.adr-val { font-size:14px; font-weight:700; color:#5B21B6; }

/* ── Two / Three col ────────────────────────────────────────── */
.two-col   { display:grid; grid-template-columns:1fr 1fr;     gap:10px; }
.three-col { display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px;  }

/* ── Info box ───────────────────────────────────────────────── */
.info-box {
    background:#F8FAFC; border:2px solid #E2E8F0; border-radius:14px;
    padding:12px 16px; margin-bottom:12px;
    font-size:13px; font-weight:600; color:#64748B; line-height:1.5;
}
.info-blue   { background:#EFF6FF; border-color:#BFDBFE; color:#1D4ED8; }
.info-purple { background:#F5F3FF; border-color:#DDD6FE; color:#5B21B6; }

/* ── Loading ────────────────────────────────────────────────── */
.loading-row { display:flex; align-items:center; gap:10px; color:#94A3B8; padding:16px 0; font-size:14px; }
.spinner {
    display:inline-block; width:20px; height:20px;
    border:2px solid #E2E8F0; border-top-color:#2563EB;
    border-radius:50%; animation:spin 0.7s linear infinite; flex-shrink:0;
}
@keyframes spin { to { transform:rotate(360deg); } }

/* ── Errors ─────────────────────────────────────────────────── */
.err-summary {
    background:#FEF2F2; border:2px solid #FEE2E2; border-radius:16px;
    padding:16px; margin-bottom:14px;
}
.err-summary-title { font-size:13px; font-weight:900; color:#B91C1C; margin-bottom:12px; }
.err-row   { display:flex; align-items:flex-start; gap:10px; margin-bottom:8px; }
.err-row:last-child { margin-bottom:0; }
.err-dot   { width:7px; height:7px; border-radius:50%; background:#F87171; flex-shrink:0; margin-top:4px; }
.err-text  { font-size:13px; font-weight:600; color:#B91C1C; flex:1; line-height:1.4; }
.err-inline{ font-size:12px; font-weight:700; color:#EF4444; margin-top:6px; }
.char-count { font-size:10px; font-weight:700; color:#94A3B8; text-align:right; margin-top:4px; }
.cc-red { color:#EF4444; }
</style>